import React, { useMemo } from 'react';
import { Button, Dropdown } from 'antd';
import { AuditOutlined, EditOutlined, EllipsisOutlined, RightOutlined, ScanOutlined } from '@ant-design/icons';
import styles from './CompatibilityTable.module.scss';
import { useTranslation } from 'react-i18next';
import { RouteKey } from '../../../../config/page-key';
import { useHistory } from 'react-router-dom';
import { useCan } from '../../../../features/user/Can';
import { Permission } from '../../../../features/user/Permission';
import { getBusType, formatYearList } from '../../VehicleModelHelper';
import useScanRequestState from '../../../../hooks/useScanRequestState';

const VehicleInfo = ({ record, onSelectVehicleModel }) => {
  const { make, model, yearList } = record;
  const history = useHistory();
  const { i18n, t } = useTranslation();
  const can = useCan();

  const { getScanRequestState, handleVote, handleUnvote } = useScanRequestState({ i18n, t });

  const year = formatYearList(yearList);

  const menuItems = useMemo(() => {

    // Create audit children for each source-busType combination
    const auditChildren = record.allSourceAndBusTypePairs.map((row, index) => {return{
      key: `audit-${index}`,
      label: `${row.source} - ${getBusType(row)}`,
      onClick: () => {
        let vehicleModelIds = (record.fullGroupModels).filter(i => i.source === row.source).map(i => i.id).filter((value, index, self) => self.indexOf(value) === index)
        history.push(RouteKey.AUDIT_VEHICLE_MODEL, { id: vehicleModelIds, name: `${make} ${model}` })
      }
    }});

    // Create edit children for each source-busType combination  
    const editChildren = record.allSourceAndBusTypePairs.map((row, index) => ({
      key: `edit-${index}`,
      label: `${row.source} - ${getBusType(row)}`,
      onClick: () => {
        let vehicleModelIds = (record.fullGroupModels).filter(i => i.source === row.source).map(i => i.id).filter((value, index, self) => self.indexOf(value) === index)
        if (vehicleModelIds.length > 1) {
          history.push(RouteKey.VEHICLE_MODEL_GROUP, { record: vehicleModelIds });
        } else {
          history.push(RouteKey.VEHICLE_MODEL, { record: row });
        }
      }
    }));

    const scanState = getScanRequestState(record);

    const items = [
      {
        available: scanState?.isEligibleForScanRequest,
        key: 'scan',
        icon: <ScanOutlined />,
        label: scanState?.isUnvotable 
          ? t("VehicleModel.PollClosed", { status: t('VehicleModel.RequestStatus.' + scanState.unvotablePoll.status) })
          : scanState?.hasVoted 
            ? t("VehicleModel.CancelRequest") 
            : t("VehicleModel.RequestScan"),
        disabled: scanState?.isUnvotable,
        onClick: async () => {
          if (scanState?.hasVoted) {
            await handleUnvote(record.id);
          } else {
            await handleVote(record.id);
          }
        },
      },
      {
        available: can({ everyPermission: [Permission.AUDIT] }),
        key: 'audit',
        icon: <AuditOutlined />,
        label: t("Audits.Audits"),
        children: auditChildren
      },
      {
        available: can({ everyPermission: [Permission.COMMON_EDIT] }),
        key: 'edit',
        icon: <EditOutlined />,
        label: t("Common.Edit"),
        children: editChildren
      },
      {
        available: true,
        key: 'showMore',
        label: <span>{t("Common.ShowMore")} <RightOutlined /></span>,
        onClick: () => onSelectVehicleModel(record.vehicleIndex),
      }
    ];

    return items.filter(i => i.available).map(({ available, ...item }) => item);
  }, [can, record, make, model, history, getScanRequestState, handleVote, handleUnvote, t]);

  return (
    <>
      <Dropdown
        placement="bottomLeft"
        menu={{
          items: menuItems
        }}
      >
        <Button icon={<EllipsisOutlined />} size="small" />
      </Dropdown>

      <div className={styles.vehicleInfo}>
        <div>{`${make} ${model}`}</div>
        <br />
        <div>{year}</div>
      </div>
    </>
  )
};

export default VehicleInfo;